// tslint:disable:max-classes-per-file

import { Components } from "appworks/components/components";
import { loader } from "appworks/loader/loader";
import { gameState } from "appworks/model/game-state";
import { Model } from "appworks/model/model";
import { Services } from "appworks/services/services";
import { stateMachine } from "appworks/state-machine/state-machine";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Sequence } from "appworks/utils/contracts/sequence";
import { deviceInfo } from "appworks/utils/device-info";
import { Rectangle } from "appworks/utils/geom/rectangle";
import Logger = require("js-logger");
import { Signal } from "signals";
import { AbstractMatrixComponent } from "slotworks/components/matrix/abstract-matrix-component";
import { slotDefinition } from "slotworks/model/slot-definition";
import { slotModel } from "slotworks/model/slot-model";
import { ygg } from "ygg/ygg";
import { PartnerConnectCollectCommand } from "./commands/partner-connect-collect-command";
import { PartnerConnectEvents } from "./partner-connect-events";
import { partnerConnectConnector } from "./server/connectors/partner-connect-connector";
import { PartnerConnectCurrency } from "./services/partner-connect-currency";
import { PartnerConnectTranslations } from "./services/partner-connect-translations";

declare const __DEBUG__: boolean;
export declare namespace PartnerConnect {
    class ClockTime {
        public static getTime(): string;
    }
    class SlotGame {
        public load(loadConfig: {
            companyName: string;
            isGameTitleAlwaysVisible: boolean;
            pcFeatures: { areEventsReplayEnabled: boolean };
            renderTo: HTMLElement;
            boost?: BoostConfig;
            theme?: any;
        }): Promise<SlotGameConfig>;
        public subscribeOnEvents(events: {
            closeGameSession: () => boolean;
            disableAudio: () => void;
            enableAudio: () => void;
            errorPopupClosed: (id: string) => void;
            errorPopupOpened: (id: string) => void;
            getAudioVolume: () => number;
            getBalance: () => number;
            initSpin: () => void;
            openGameRules: () => void;
            pauseGame: () => void;
            resize: () => void;
            resumeGame: () => void;
            setMinBet: () => void;
            skipSplashScreen: () => void;
            skipVideoIntro: () => void;
            stopAutoSpin: () => void;
            stopReplay: () => void;
            updateGameBalance: (balance: { cash: number, won?: boolean }) => void;
            uiChanged: (displayedElements: IUiElement[]) => void;
        }): void;
        public getGameEventsEmitter(): EventEmitters;
        public uiComponents(): SlotUI;
        public getWagerHelper(): {
            getPrepaidInfo(): {
                betValue?: number;
                isPrepaid: boolean;
                type?: "EXTERNAL" | "INTERNAL";
            };
        };

        public spin(data: SpinRequest): Promise<SpinResponse>;
        public play(data: SpinRequest): Promise<SpinResponse>;
        public collect(data: { step: number }): Promise<SpinResponse>;
    }

    interface BoostConfig {
        buttonPosition?: string;
        disabled?: boolean;
        renderTo?: HTMLElement;
    }

    interface SpinRequest {
        amount: number;
        coin: number;
        coins: number;
        lines: number;
        [key: string]: string | number | boolean;
    }

    interface IUiElement {
        id: string;
        bounds: Rectangle;
    }

    class SpinResponse {
        public resultBal: {
            cash: number
        };
        public wager: SpinResponseWager;
        public prepaid: SpinResponsePrepaid;
        public missionState?: SpinResponseMissionState;
    }

    class SpinResponseWager {
        public bets: SpinResponseBet[];
        public prepaid: SpinResponsePrepaid;
        public status: string;
        public timestamp: string;
        public wagerid: string;
    }

    class SpinResponsePrepaid {
        public collect: {
            rounds: number;
            win: number;
        };
        public state: {
            bet: {
                amount: number;
                prepaidId: string;
            };
            left: number;
            reject: any;
            rejectReason: any;
            win: number;
        };
    }

    class SpinResponseMissionState {
        public autoResetPerformed: boolean;
        public resetPossible: boolean;
        public bestScore: number;
        public score: number;
        public objectives: {
            [key: string]: SpinResponseMissionStateObjective;
        };
    }

    class SpinResponseMissionStateObjective {
        public total: number;
        public completed: number;
        public spins: number;
        public details: string;
    }

    class SpinResponseBet {
        public buyBal: {
            cash: number;
        };
        public resultBal: {
            cash: number;
        };
        public cashRace: {
            hasWon: boolean;
            prize?: number;
            initialPrize?: number;
            currency?: number;
        };
        public wager: {
            prepaid: boolean;
            status: string;
            wagerid: number;
            bets: Array<{
                step: number;
                status: string;
                wonamount: number;
                betamount: number;
                prizes: object;
                betdata?: {
                    accC: number;
                    accWa: number;
                    cashbet: number;
                    cheat: string;
                    cmd: string;
                    coin: number;
                    initialBalance: number;
                };
                eventdata: {
                    accC: number;
                    accWa: number;
                    response: {
                        cashWin: number;
                        coinWin: number;
                        clientData: any;
                    };
                };
            }>;
        };
        // TODO: Determine whether removing the following data will break things horribly
        public betAmount: number;
        public betdata: {
            cmd: string;
            cashBet: number;
            coin: number;
            initialBalance: number;
            ncoins?: number;
        };
        public eventdata: {
            finalBoard: string[][];
            freeSpinsBonusSpins?: number;
            iCheat: string;
            initialBalance: number;
            nextCmds: string;
            reelSet: string;
            reels: string[][];
            resultBalance: string | number;
            wonCoins: number;
            wonMoney: number;
            prizes: any;
            status: string;
            wonamount: number;
            wtw: Array<Array<string | number>>;

            [key: string]: any;
        };
        public wonamount: string;
        public step: number;
    }

    class SlotGameConfig {
        public mode: "GAME" | "RESTORE" | "REPLAY" | "REWATCH";
        public settings: {
            balance: number;
            autoplayLevels: number[];
            currency: string;
            defaultBetValue: number;
            hasLossLimit: boolean;
            isAutoSpinsDisabled: boolean;
            isBuyBonusDisabled: boolean;
            isFeaturePanelRequired: boolean;
            isGameHistoryEnabled: boolean;
            language: string;
            menuVisibility: MenuVisibility;
            minimalSpinningTime: number;
            organizationConfig: {
                DefaultBetType: string;
                DefaultDenom: string;
                Denoms: string;
                type: string;
            };
            preloader: {
                delay: number;
                hasMessage: boolean;
                message?: string;
            };
            shouldShowClock: "yes" | "no";
            shouldShowGameName: boolean;
            slotConfig: {
                AllowedFirstCommands: Array<{
                    cmd: string,
                    coinsPerBet: number,
                    currencyGroupTypeName: string
                }>;
                CoinsPerBet: number[];
                Denoms: string;
                NumberOfReels: number;
                WindowHeight: number;
                serverType: string;

                [key: string]: any;
            };
        };
        public wagers: {
            data: SpinResponseWager[];
            empty: boolean;
        };
        public prepaids: {
            data: SpinResponsePrepaid[];
        };
        public translations: {
            game: any;
            shared: any;
        };
        public customParameters: {
            disableSkip: "yes" | "no";
            disableMenuPaytable: "yes" | "no";
            disableMenuSettings: "yes" | "no";
            disableMenuRules: "yes" | "no";
            disableMenuAudio: "yes" | "no";
            assetsURL: string;
            license: string;
            [key: string]: any;
        };
    }

    interface MenuVisibility {
        isGameHistoryVisible: boolean;
        isMenuAudioVisible: boolean;
        isMenuDepositVisible: boolean;
        isMenuFullScreenVisible: boolean;
        isMenuHomeVisible: boolean;
        isMenuPaytableVisible: boolean;
        isMenuRulesVisible: boolean;
        isMenuSettingsVisible: boolean;
    }

    class EventEmitters {
        public balanceChanged: (data: any) => Promise<any>;
        public customEvent: (eventName: string, data?: any) => Promise<any>;
        public gameLoaderReady: () => Promise<any>;
        public gameLoadingEnded: () => Promise<any>;
        public gameLoadingError: () => Promise<any>;
        public gameLoadingProgress: (progress: number) => Promise<any>;
        public gameLoadingStarted: () => Promise<any>;
        public gameReady: (gameReadyData: { defaultBetValue: number, isUnfinishedUserChoice?: boolean }) => Promise<{
            nextPrepaid: { amount: number }, restoreMode: "NONE" | "RESTORE" | "SKIP"
        }>;
        public gameRoundEnded: () => Promise<{ nextPrepaid: number }>;
        public gameRoundStarted: () => Promise<any>;
        public introReady: () => Promise<any>;
        public redirectUrl: (data: string) => Promise<any>;
        public spinEnded: () => Promise<any>;
        public spinError: () => Promise<any>;
        public spinStarted: () => Promise<any>;
        public totalBetChanged: (betValue: number) => Promise<void>;
        public willClosingGameLeaveUnfinishedResponse: (isNotIdle: boolean) => Promise<any>;
    }

    class SlotUI {
        public clickActionHandler: () => { deposit: () => Promise<void>, home: () => void, maximumBetWarning: (maximumBet: number) => Promise<void> };
        public showGameHistory: (gameHistoryProperties: { theme?: any }) => Promise<void>;
        public showPopup: (properties: {
            message: string,
            title?: string,
            primaryButtonText?: string,
            primaryButtonAction?: Function,
            secondaryButtonText?: string,
            secondaryButtonAction?: Function,
            optionalButtonText?: string,
            optionalButtonAction?: Function,
            theme?: any
        }) => void;
    }

    class CurrencyFormatter {
        public format(value: number): string;
        public formatDecimal(value: number, decimals?: number): string;
        public formatNumber(value: number): string;
        public formatStringWithCurrency(value: number): string;
        public formatWithCurrency(value: number, decimals?: number): string;
        public getCurrencySymbol(): string;
        public isCurrencySymbolOnTheLeft(): boolean;
    }

    class PreparedObjectsFactory {
        public static getCurrencyFormatter(): PartnerConnect.CurrencyFormatter;
    }

    interface Version {
        major: number;
        minor: number;
        patch: number;
    }

    function isVersionSupported(version: Version): boolean;
}

interface OverrideAutoplayCounts {
    defaultIndex: number;
    normal: number[];
    lossLimitEnabled: number[];
}

export enum BoostButtonPosition {
    LEFT_CENTER = "LEFT_CENTER",
    LEFT_TOP = "LEFT_TOP",
    LEFT_BOTTOM = "LEFT_BOTTOM",
    RIGHT_CENTER = "RIGHT_CENTER",
    RIGHT_TOP = "RIGHT_TOP",
    RIGHT_BOTTOM = "RIGHT_BOTTOM",
    TOP_LEFT = "TOP_LEFT",
    TOP_CENTER = "TOP_CENTER",
    TOP_RIGHT = "TOP_RIGHT",
    BOTTOM_LEFT = "BOTTOM_LEFT",
    BOTTOM_CENTER = "BOTTOM_CENTER",
    BOTTOM_RIGHT = "BOTTOM_RIGHT"
}

export abstract class PartnerConnectManager {
    public static GAME_ID: number;
    public static GAME_NAME: string;
    public static GAME_APP_SRV: string;
    public static COMPANY_NAME: string;
    public static EXPECTED_VERSION?: PartnerConnect.Version;
    public static OVERRIDE_AUTOPLAY_COUNTS?: OverrideAutoplayCounts;
    public static OVERRIDE_AUTOPLAY_LOSS_LIMITS?: OverrideAutoplayCounts;
    public static OVERRIDE_AUTOPLAY_SINGLE_WIN_LIMITS?: OverrideAutoplayCounts;
    public static OVERRIDE_INITIAL_GRID?: string[][];
    public static BOOST_BUTTON_POSITION?: string;
    public static OVERRIDE_PARAMS_DEBUG: any = {};
    public static onSkipSplashScreen: Signal = new Signal();
    public static skipSplashScreen: boolean = false;
    public static prepaidData: PartnerConnect.SpinResponsePrepaid;

    // Custom data object to store values for popup at start / end of history replays
    public static historyData: {
        wagerid?: string;
        bet?: number;
        win?: number;
        startBalance?: number;
        endBalance?: number;
        timestamp?: string;
    };

    public static load(): Contract<void> {
        Services.CurrencyType = PartnerConnectCurrency;
        Services.TranslationsType = PartnerConnectTranslations;

        return new Sequence([
            () => this.setCustomParameters(),
            () => this.loadScript(),
            () => this.initialise()
        ]);
    }

    public static ready(): void {
        Services.bet.onBetChange.add(() => PartnerConnectManager.getEmitters().totalBetChanged(Services.bet.getTotalStake() / 100));

        PartnerConnectManager.prepaidUpdate();

        window.addEventListener("unload", () => this.getEmitters().willClosingGameLeaveUnfinishedResponse(!uiFlags.has(UIFlag.IDLE)));

        Logger.debug("PartnerConnect", "ready");
    }

    public static sendMessage(id: string, data?: any): void {
        const iframe: HTMLIFrameElement = document.querySelector("iframe");

        const message = (data) ? { event: id, data } : id;

        if (iframe) {
            iframe.contentWindow.postMessage(message, "*");
        } else {
            window.postMessage(message, "*");
        }

        Logger.debug("PartnerConnect", "message", message);
    }

    public static prepaidUpdate() {
        const prepaidInfo = this.slot.getWagerHelper().getPrepaidInfo();

        const model = slotModel.read();

        if (prepaidInfo.isPrepaid && prepaidInfo.betValue) {
            uiFlags.set(UIFlag.FREE_BETS, true);
            gameState.autoDeductStake = false;
            model.bet.creditSizes.currentValue = Math.round(prepaidInfo.betValue * 100);
        } else if (uiFlags.has(UIFlag.FREE_BETS)) {
            if (!prepaidInfo.isPrepaid || (PartnerConnectManager.prepaidData?.collect && !PartnerConnectManager.prepaidData?.state)) {
                model.bet.creditSizes.currentValue = model.bet.creditSizes.defaultValue;
                uiFlags.set(UIFlag.FREE_BETS, false);
                gameState.autoDeductStake = true;
            }
        }

        slotModel.write(model);
    }

    public static getConfig(): PartnerConnect.SlotGameConfig | undefined {
        return this.config;
    }

    public static getEmitters(): PartnerConnect.EventEmitters | undefined {
        return this.slot.getGameEventsEmitter();
    }

    public static getUI(): PartnerConnect.SlotUI | undefined {
        return this.slot.uiComponents();
    }

    public static formatCurrency(value: number): string {
        return PartnerConnect.PreparedObjectsFactory.getCurrencyFormatter().format(value / 100);
    }

    public static getCurrencyFormatter(): PartnerConnect.CurrencyFormatter {
        return PartnerConnect.PreparedObjectsFactory.getCurrencyFormatter();
    }

    public static getGame(): PartnerConnect.SlotGame {
        return this.slot;
    }

    public static getTranslations(): Map<string, string> {
        const translations = new Map<string, string>();

        for (const key in this.config.translations.shared) {
            if (this.config.translations.shared[key] !== undefined) {
                const id = key.toLowerCase().replace(/ /g, "_");
                translations.set(id, this.config.translations.shared[key]);
            }
        }

        for (const key in this.config.translations.game) {
            if (this.config.translations.game[key] !== undefined) {
                const id = key.toLowerCase().replace(/ /g, "_");
                translations.set(id, this.config.translations.game[key]);
            }
        }

        return translations;
    }

    public static setInitialGrid(): void {
        if (this.config) {
            const reels: string[][] = this.OVERRIDE_INITIAL_GRID || this.config.settings.slotConfig.Reels;
            try {
                Components.get(AbstractMatrixComponent).jumpToGrid(reels.map((reel) => reel.map((symbol) => slotDefinition.symbolDefinitions[ygg.symbolMap.get(symbol)].id)));
            } catch (e) {
                Logger.error("FAILED TO SET INITIAL GRID");
                Logger.error(e);
            }
        }
    }

    public static gameReady() {
        return new Contract<void>((resolve) => {
            PartnerConnectManager.getEmitters()?.gameReady({ defaultBetValue: Services.bet.getTotalStake() }).then((value) => {
                if (value.restoreMode === "SKIP") {
                    PartnerConnectEvents.onBalanceChanged.addOnce(() => {
                        Services.transaction.setBalance(gameState.getCurrentGame().balance);
                    });
                    PartnerConnectCollectCommand(true).then(() => {
                        Contract.wrapPromise(PartnerConnectManager.getEmitters()?.gameRoundEnded()).execute();
                    });
                } else if (value.restoreMode === "RESTORE") {
                    gameState.getCurrentGame().isRecovered = true;
                }

                resolve();
            });
        });
    }

    public static getNextWageData(): PartnerConnect.SpinResponse | null {
        if (this.config && this.config.wagers.data.length > 0) {
            if (this.isHistoryMode()) {
                if (this.config.wagers.data[0].bets.length > 0) {
                    if (this.config.wagers.data[0].bets[0].eventdata) {
                        const wager: PartnerConnect.SpinResponseWager = {
                            wagerid: this.config.wagers.data[0].wagerid,
                            prepaid: this.config.wagers.data[0].prepaid,
                            status: this.config.wagers.data[0].status,
                            timestamp: this.config.wagers.data[0].timestamp,
                            bets: [this.config.wagers.data[0].bets.shift()]
                        };
                        const prepaid: PartnerConnect.SpinResponsePrepaid = {
                            collect: this.config.prepaids?.data[0].collect,
                            state: this.config.prepaids?.data[0].state
                        };
                        return { wager, resultBal: { cash: 0 }, prepaid };
                    } else {
                        this.config.wagers.data[0].bets.shift();
                        return this.getNextWageData();
                    }
                } else {
                    return null;
                }
            } else {
                const wager: PartnerConnect.SpinResponseWager = this.config.wagers.data.shift();
                const prepaid: PartnerConnect.SpinResponsePrepaid = this.config.prepaids?.data.shift();
                return { wager, resultBal: { cash: 0 }, prepaid };
            }
        } else {
            return null;
        }
    }

    public static isHistoryMode(): boolean {
        return this.config.mode === "REPLAY" || this.config.mode === "REWATCH";
    }

    public static isHistoryPending(): boolean {
        if (this.config && this.config.wagers.data.length > 0) {
            if (this.isHistoryMode()) {
                if (this.config.wagers.data[0].bets.length > 0) {
                    if (this.config.wagers.data[0].bets[0].eventdata?.response) {
                        return true;
                    } else {
                        this.config.wagers.data[0].bets.shift();
                        return this.isHistoryPending();
                    }
                }
            }
        }
        return false;
    }

    private static slot?: PartnerConnect.SlotGame;
    private static config?: PartnerConnect.SlotGameConfig;

    private static setCustomParameters(): Contract<void> {
        return new Contract((resolve) => {
/////////////////////////////
////////////////////////////////////////////////////
/////////////////////////////////////////////
////////////////////////////
////////////////////////////////
///////////////////////////
//////////////////////////////////////////////////////
///////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////
////////////////////////////////////////////
//////////////////////////////////////////
///////////////////////////////////////
//////////////////////////////////////////
/////////////////////////////////////
//////////////////////////////
/////////////////////////////
//////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////
///////////////
///////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////
/////////////
//////////////////////
            resolve();
        });
    }

    private static loadScript(): Contract<void> {
        return new Contract((resolve) => {
            const element = document.createElement("script") as HTMLScriptElement;
            document.getElementById("all").appendChild(element);
            element.onload = () => {
                Logger.debug("PartnerConnect", "loaded");
                resolve();
            };
            element.src = this.getURL();
        });
    }

    private static initialise(): Contract<void> {
        return new Contract((resolve) => {
            window.addEventListener("message", (event) => {
                Logger.debug("PartnerConnect", "message", event.data);
            });

            let buttonPosition: string = PartnerConnectManager.BOOST_BUTTON_POSITION;
            if (!buttonPosition) {
                buttonPosition = deviceInfo.isDesktop ? "LEFT_CENTER" : "LEFT_BOTTOM";
            }

            if (this.compareVersion()) {
                Logger.debug("PartnerConnect", "creating slot");
                this.slot = new PartnerConnect.SlotGame();
                this.slot.subscribeOnEvents(new PartnerConnectEvents());
                Logger.debug("PartnerConnect", "loading config...");
                this.slot.load({
                    companyName: this.COMPANY_NAME,
                    pcFeatures: { areEventsReplayEnabled: false },
                    isGameTitleAlwaysVisible: true,
                    renderTo: document.getElementById("partner-connect"),
                    boost: { renderTo: document.getElementById("boost"), buttonPosition }
                }).then((config) => {
                    Logger.debug("PartnerConnect", "config", config);
                    this.config = config;

                    const settings = Model.read().settings;
                    settings.language = config.settings.language;
                    Model.write({ settings });

                    if (this.config.customParameters.assetsURL) {
                        loader.setBaseURL(this.config.customParameters.assetsURL);
                    }

                    const preloaderMessageElement: HTMLElement = document.getElementById("license_message");
                    if (preloaderMessageElement && this.config.settings.preloader.hasMessage) {
                        preloaderMessageElement.innerText = this.config.settings.preloader.message;
                        const licenseEl = document.getElementsByClassName("license_message")[0] as HTMLElement;
                        if (licenseEl) {
                            licenseEl.style.display = "flex";
                        }
                    }

                    const gcLogoElement: HTMLElement = document.getElementById("gc_logo");
                    if (gcLogoElement && this.config.settings.preloader.hasMessage) {
                        gcLogoElement.style.display = "flex";
                    }

                    partnerConnectConnector.pendingCmds = this.config?.settings?.slotConfig?.AllowedFirstCommands?.map((x: any) => x.cmd);

//////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////
/////////////////////////////////////////////////////////
//////////////////////////////

                    new Sequence([
                        () => Contract.wrapPromise(this.getEmitters().gameLoaderReady()),
                        () => Contract.wrapPromise(this.getEmitters().gameLoadingStarted()),
                        () => Contract.wrap(() => {
                            Logger.debug("PartnerConnect", "initialised");
                            resolve();
                        })
                    ]).execute();
                }
                );
            } else {
                // TODO: Something here if the version isn't valid...
                Logger.debug("PartnerConnect", "version mismatch");
                Logger.error("Version mismatch in PartnerConnect");
            }
        });
    }

    private static compareVersion(): boolean {
/////////////////////////
////////////////////////
////////////////////////
/////////
//////////////////
        return PartnerConnect.isVersionSupported(this.EXPECTED_VERSION);
    }

    private static getPostParams(): any | undefined {
        const params = (window as any)._POST_PARAMS_;
//////////////////////////////////////////////////////////////////////////////////////
/////////////////////
//////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////
/////////////

////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////
/////////////
/////////
//////////////////
        return params;
    }

    private static getURL(): string {
        let pcUrl: string;
        const postParams: any | undefined = this.getPostParams();
        if (postParams && postParams.pcUrl) {
            pcUrl = postParams.pcUrl;
        } else {
            pcUrl = decodeURIComponent(window.location.search
                .substring(1)
                .split("&")
                .filter((param) => param.startsWith("pcUrl"))[0]
                .substring(6));
        }
        return pcUrl;

    }
}
